import {
  auth,
  getToken,
  setCachedUser,
  getCachedUser,
  clearSession,
  getConfig,
  mediaUrl,
} from '../api.js';
import { navigate } from '../utils/router.js';
import { toggleTheme, getTheme } from '../utils/theme.js';
import { esc, avatarUrl, toast, openHelpModal } from '../utils/dom.js';
import { renderFeed, renderSinglePost } from './feed.js';
import { renderProfile } from './profile.js';
import { renderSearch } from './search.js';
import { renderNotifications, fetchUnreadCount } from './notifications.js';
import { renderMessages } from './messages.js';
import { renderAdmin } from './admin.js';
import { openCompose } from './compose.js';
import { maybeShowPwaPrompt } from './pwa-prompt.js';

const TITLES = {
  home: '主页',
  profile: '个人资料',
  search: '搜索',
  notifications: '提醒',
  messages: '私聊',
  admin: '管理用户',
  post: '帖子',
  user: '用户资料',
};

export async function renderApp(root, route) {
  if (!getToken()) {
    navigate('/login', { replace: true });
    return () => {};
  }

  let user = getCachedUser();
  try {
    const me = await auth.me();
    user = me.user || me;
    setCachedUser(user);
  } catch (err) {
    if (err.status === 401) {
      clearSession();
      toast('登录已过期，请重新登录', 'error');
      navigate('/login', { replace: true });
      return () => {};
    }
    // keep cached user for offline shell
  }

  const isAdmin = user?.role === 'admin';
  const view = resolveView(route, user);

  let siteLogoUrl = null;
  let appConfig = null;
  try {
    appConfig = await getConfig();
    siteLogoUrl = appConfig.siteLogoUrl || appConfig.site_logo_url || null;
  } catch {
    /* ignore */
  }

  const logoHtml = siteLogoUrl
    ? `<img class="sidebar-site-logo" id="site-logo-img" src="${esc(mediaUrl(siteLogoUrl))}" alt="logo" />`
    : `<img class="sidebar-site-logo hidden" id="site-logo-img" alt="logo" />`;

  root.innerHTML = `
    <div class="app-shell">
      <div class="sidebar-overlay" id="sidebar-overlay"></div>
      <aside class="app-sidebar" id="app-sidebar">
        <div class="sidebar-brand">
          <div class="sidebar-brand-mark">GS</div>
          <span>GSORG</span>
          ${logoHtml}
        </div>
        <nav class="sidebar-nav">
          <button type="button" class="nav-item ${view.name === 'home' ? 'active' : ''}" data-nav="home">
            <span class="nav-icon">🏠</span> 主页
          </button>
          <button type="button" class="nav-item ${view.name === 'profile' && view.self ? 'active' : ''}" data-nav="profile">
            <span class="nav-icon">👤</span> 个人资料
          </button>
          <button type="button" class="nav-item ${view.name === 'search' ? 'active' : ''}" data-nav="search">
            <span class="nav-icon">🔍</span> 搜索
          </button>
          <button type="button" class="nav-item ${view.name === 'notifications' ? 'active' : ''}" data-nav="notifications" id="nav-notif">
            <span class="nav-icon">🔔</span> 提醒
            <span class="nav-badge hidden" id="notif-dot"></span>
          </button>
          <button type="button" class="nav-item ${view.name === 'messages' ? 'active' : ''}" data-nav="messages">
            <span class="nav-icon">💬</span> 私聊
          </button>
          ${
            isAdmin
              ? `<button type="button" class="nav-item ${
                  view.name === 'admin' ? 'active' : ''
                }" data-nav="admin">
                <span class="nav-icon">⚙️</span> 管理用户
              </button>`
              : ''
          }
        </nav>
        <button type="button" class="sidebar-compose" id="compose-btn" title="撰写帖子">✎</button>
        <div class="sidebar-user" id="sidebar-user">
          <img src="${esc(avatarUrl(user))}" alt="" />
          <div class="sidebar-user-meta">
            <div class="name">${esc(user?.nickname || '用户')}</div>
            <div class="gid">GID ${esc(user?.gid || '')}</div>
          </div>
        </div>
        <button type="button" class="btn btn-ghost btn-sm" id="logout-btn" style="margin:0.25rem 0.5rem">退出登录</button>
        <div class="sidebar-footer">GlobalSchoolOrganization - Yize He © 2026</div>
      </aside>
      <main class="app-main">
        <header class="app-topbar">
          <button type="button" class="hamburger" id="hamburger" aria-label="菜单">☰</button>
          <h1 id="view-title">${esc(view.title)}</h1>
          <div class="topbar-actions">
            <button type="button" class="help-btn" id="help-btn" title="认证与帮助">?</button>
            <button type="button" class="theme-toggle-btn" id="theme-toggle" title="切换主题">
              ${getTheme() === 'light' ? '🌙' : '☀️'}
            </button>
          </div>
        </header>
        <div class="app-content" id="app-content"></div>
      </main>
    </div>
  `;

  const sidebar = root.querySelector('#app-sidebar');
  const overlay = root.querySelector('#sidebar-overlay');
  const content = root.querySelector('#app-content');

  const closeSidebar = () => {
    sidebar.classList.remove('open');
    overlay.classList.remove('open');
  };
  const openSidebar = () => {
    sidebar.classList.add('open');
    overlay.classList.add('open');
  };

  root.querySelector('#hamburger')?.addEventListener('click', openSidebar);
  overlay?.addEventListener('click', closeSidebar);

  root.querySelectorAll('[data-nav]').forEach((btn) => {
    btn.addEventListener('click', () => {
      closeSidebar();
      const nav = btn.dataset.nav;
      if (nav === 'home') navigate('/app');
      else if (nav === 'profile') navigate(`/user/${user.gid}`);
      else if (nav === 'search') navigate('/search');
      else if (nav === 'notifications') navigate('/notifications');
      else if (nav === 'messages') navigate('/messages');
      else if (nav === 'admin') navigate('/admin');
    });
  });

  root.querySelector('#sidebar-user')?.addEventListener('click', () => {
    closeSidebar();
    navigate(`/user/${user.gid}`);
  });

  root.querySelector('#compose-btn')?.addEventListener('click', () => {
    closeSidebar();
    openCompose({
      onDone: () => {
        if (view.name === 'home') renderFeed(content, { currentUser: user });
      },
    });
  });

  root.querySelector('#theme-toggle')?.addEventListener('click', () => {
    toggleTheme();
    root.querySelector('#theme-toggle').textContent = getTheme() === 'light' ? '🌙' : '☀️';
  });

  root.querySelector('#help-btn')?.addEventListener('click', () => {
    openHelpModal(appConfig || {});
  });

  const onLogo = (e) => {
    const url = e.detail?.siteLogoUrl;
    const img = root.querySelector('#site-logo-img');
    if (!img) return;
    if (url) {
      img.src = mediaUrl(url);
      img.classList.remove('hidden');
    } else {
      img.removeAttribute('src');
      img.classList.add('hidden');
    }
  };
  window.addEventListener('gsorg:logo', onLogo);

  root.querySelector('#logout-btn')?.addEventListener('click', () => {
    clearSession();
    navigate('/login', { replace: true });
  });

  // unread badge
  const updateDot = async () => {
    const count = await fetchUnreadCount();
    const dot = root.querySelector('#notif-dot');
    if (dot) dot.classList.toggle('hidden', !count);
    return count;
  };
  updateDot();

  // PWA prompt after login
  setTimeout(() => maybeShowPwaPrompt(), 600);

  // Render view
  const onUserUpdate = (u) => {
    user = u;
    const img = root.querySelector('#sidebar-user img');
    const name = root.querySelector('#sidebar-user .name');
    const gid = root.querySelector('#sidebar-user .gid');
    if (img) img.src = avatarUrl(u);
    if (name) name.textContent = u.nickname || '用户';
    if (gid) gid.textContent = `GID ${u.gid || ''}`;
  };

  if (view.name === 'home') {
    await renderFeed(content, { currentUser: user });
  } else if (view.name === 'profile' || view.name === 'user') {
    await renderProfile(content, view.gid, { currentUser: user, onUserUpdate });
  } else if (view.name === 'search') {
    renderSearch(content, { currentUser: user });
  } else if (view.name === 'notifications') {
    await renderNotifications(content, {
      onRead: () => updateDot(),
    });
  } else if (view.name === 'messages') {
    await renderMessages(content, view.peerGid, { currentUser: user });
  } else if (view.name === 'admin') {
    if (!isAdmin) {
      content.innerHTML = `<div class="empty-state"><p>无权限</p></div>`;
    } else {
      await renderAdmin(content);
    }
  } else if (view.name === 'post') {
    await renderSinglePost(content, view.postId, { currentUser: user });
  } else {
    content.innerHTML = `<div class="empty-state"><p>未知页面</p></div>`;
  }

  return () => {
    window.removeEventListener('gsorg:logo', onLogo);
    closeSidebar();
  };
}

function resolveView(route, user) {
  const segs = route.segments || [];
  const first = segs[0] || 'app';

  if (first === 'app' || first === 'home' || first === '') {
    return { name: 'home', title: TITLES.home, self: true };
  }
  if (first === 'search') return { name: 'search', title: TITLES.search };
  if (first === 'notifications') return { name: 'notifications', title: TITLES.notifications };
  if (first === 'messages') {
    return {
      name: 'messages',
      title: TITLES.messages,
      peerGid: segs[1] ? decodeURIComponent(segs[1]) : null,
    };
  }
  if (first === 'admin') return { name: 'admin', title: TITLES.admin };
  if (first === 'post' && segs[1]) {
    return { name: 'post', title: TITLES.post, postId: segs[1] };
  }
  if (first === 'user' && segs[1]) {
    const gid = decodeURIComponent(segs[1]);
    const self = user && String(user.gid) === String(gid);
    return {
      name: self ? 'profile' : 'user',
      title: self ? TITLES.profile : TITLES.user,
      gid,
      self,
    };
  }
  if (first === 'profile') {
    return {
      name: 'profile',
      title: TITLES.profile,
      gid: user?.gid,
      self: true,
    };
  }
  return { name: 'home', title: TITLES.home };
}
